import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/mocks/films";
import { typeLabel } from "@/utils/film";

interface HeroCarouselProps {
  films: Film[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
}

export default function HeroCarousel({ films, favorites, onToggleFavorite }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = films.length;

  const go = useCallback(
    (dir: number) => setIndex((prev) => (prev + dir + count) % count),
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const film = films[index];
  const favorited = favorites.includes(film.id);

  return (
    <section className="relative overflow-hidden bg-primary-900">
      <div className="absolute inset-0">
        <img
          key={film.id}
          src={film.poster}
          alt=""
          aria-hidden="true"
          className="h-full w-full scale-110 object-cover object-top opacity-40 blur-md"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-900/95 to-primary-800/80"></div>
      </div>

      <div className="relative mx-auto flex min-h-[520px] w-full max-w-6xl flex-col gap-8 px-4 py-12 md:px-6 md:py-16 lg:min-h-[560px] lg:flex-row lg:items-center">
        <div key={film.id} className="animate-fade-up flex-1">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line"></i>
            本周焦点
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl lg:text-5xl">
            {film.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-primary-100">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-400"></i>
              <strong className="text-background-50">{film.rating.toFixed(1)}</strong>
            </span>
            <span>{film.year}</span>
            <span>{film.region}</span>
            <span>{film.runtime}</span>
            <span className="rounded-md bg-primary-700 px-2 py-0.5 text-xs text-primary-100">
              {typeLabel(film.type)}
            </span>
          </div>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
            {film.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${film.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <button
              type="button"
              onClick={() => onToggleFavorite(film.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? "border-accent-400 bg-accent-500/20 text-background-50"
                  : "border-primary-300/60 text-background-50 hover:bg-primary-700/60"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <Link
              to={`/category/${film.type}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm text-primary-100 transition-colors hover:text-background-50"
            >
              更多{typeLabel(film.type)}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="relative w-full max-w-[280px] shrink-0 lg:max-w-[320px]">
          <Link to={`/detail/${film.id}`} className="block">
            <div className="overflow-hidden rounded-lg border border-primary-300/40">
              <img
                key={film.id}
                src={film.poster}
                alt={`${film.title} 海报`}
                className="animate-fade-in h-[380px] w-full object-cover object-top lg:h-[440px]"
              />
            </div>
          </Link>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {films.map((f, i) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`切换到 ${f.title}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index ? "w-6 bg-accent-400" : "w-1.5 bg-primary-300/60 hover:bg-primary-200"
                  }`}
                ></button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="上一部"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-primary-300/50 text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="下一部"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-primary-300/50 text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}